import { useState } from "react";
import type { CustomerDto, InvoiceDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";

export function PaymentDialog({
  open,
  onOpenChange,
  customers,
  invoices,
  onRecorded,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  customers: CustomerDto[];
  invoices: InvoiceDto[];
  onRecorded(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [customerId, setCustomerId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const openInvoices = invoices.filter(
    (i) => i.status === "issued" || i.status === "partially_paid"
  );

  const submit = async () => {
    setBusy(true);
    try {
      await api.recordPayment({
        customerId,
        invoiceId: invoiceId || "",
        amount,
        method,
        reference,
      });
      push(t("finance.paymentRecordedToast"));
      setAmount("");
      setReference("");
      setInvoiceId("");
      onOpenChange(false);
      onRecorded();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("finance.recordPaymentTitle")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={busy || !customerId || !amount}
          >
            {t("finance.recordPayment")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <SelectInput
          label={t("common.fields.customer")}
          options={customers.map((c) => ({
            value: c.id,
            label: c.displayName || c.legalName,
          }))}
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
        />
        <SelectInput
          label={t("finance.invoiceOptional")}
          options={[
            { value: "", label: t("finance.unappliedPayment") },
            ...openInvoices.map((i) => ({
              value: i.id,
              label: `${i.number} · ${i.customerName}`,
            })),
          ]}
          value={invoiceId}
          onChange={(e) => setInvoiceId(e.target.value)}
        />
        <TextInput
          label={t("common.fields.amount")}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label={t("common.fields.method")}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          />
          <TextInput
            label={t("common.fields.reference")}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
